import { useMemo } from 'react'
import { endOfDay, format, isToday, startOfDay } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { useDateStore } from '@/components/DateInput'
import { createInstance } from '@/helpers/api/base'
import { useSharedRoutesStore } from '@/modules/Workstation/store'
import { Log, LogSource } from '@/types/logs'
import { useQueryClient } from '@tanstack/react-query'
import { useAppQuery } from '@/helpers/api/query'
import { useGetControllerLogParams } from '../apiHelpers/useGetControllerLogParams'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { useSearchParams } from 'react-router-dom'
import { createISOWithTimezone } from '../helpers/createIsoWithTimezone'
import { useNightShiftInfo } from '@/modules/Auth'

interface Params {
    isUnfinished?: boolean
    source?: LogSource
    useAreaRoutes?: boolean
}

export const useInvalidateControllerLogsByDate = () => {
    const queryClient = useQueryClient()
    const queryKeys = queryClient.getQueryCache().getAll()

    return (date: string) => {
        const formattedDate = format(date, 'yyyy-MM-dd')
        const queries = queryKeys.filter((query) =>
            query.queryKey.some((key) => typeof key === 'string' && key.includes(formattedDate))
        )

        queries.forEach((query) => {
            queryClient.invalidateQueries({
                queryKey: query.queryKey,
            })
        })
    }
}

export const useGetControllerLogs = ({ isUnfinished, source, useAreaRoutes = false }: Params = {}) => {
    const params = useGetControllerLogParams({ isUnfinished, source, useAreaRoutes })
    const clParams = useMemo(() => {
        return ['controllerLogs', ...params]
    }, [params])
    const { role } = useAuthStore()
    const queryClient = useQueryClient()
    const { date } = useDateStore()
    const { isNightShift, isAfterMidnight } = useNightShiftInfo()

    const ukDayStart = startOfDay(new Date(date))
    const ukDayEnd = endOfDay(new Date(date))
    if (role === Role.Controller && isNightShift && isAfterMidnight()) {
        ukDayEnd.setDate(ukDayEnd.getDate() + 1)
    }

    const dateFrom = createISOWithTimezone(
        ukDayStart.getFullYear(),
        ukDayStart.getMonth() + 1,
        ukDayStart.getDate(),
        0,
        0,
        0,
        'Europe/London'
    )

    const dateTo = createISOWithTimezone(
        ukDayEnd.getFullYear(),
        ukDayEnd.getMonth() + 1,
        ukDayEnd.getDate(),
        23,
        59,
        59,
        'Europe/London'
    )

    return useAppQuery<Log[]>({
        queryKey: clParams,
        queryFn: async ({ queryKey }) => {
            const serverLogs = await createInstance<Log[]>({
                url: '/controller-logs',
                method: 'GET',
                params: {
                    DateFrom: queryKey[6] ? null : dateFrom,
                    DateTo: queryKey[6] ? null : dateTo,
                    Routes: queryKey[4],
                    CreatedAt: queryKey[5],
                    IsUnfinished: queryKey[6],
                    Source: queryKey[7],
                    AreaCode: queryKey[8],
                },
                paramsSerializer: { indexes: null },
            })

            const cachedLogs = queryClient.getQueryData<Log[]>(clParams) ?? []

            return [
                ...serverLogs,
                ...cachedLogs.filter((cached) => !serverLogs.some((server) => server.id === cached.id)),
            ]
        },
        refetchInterval: 1000 * 60, // Refetch every 60 seconds
        refetchIntervalInBackground: true, // Continue polling even if the tab is inactive
        refetchOnMount: false,
        enabled: params && params.length >= 4 && !!params[3] && (params[3] as string[]).length > 0,
    })
}

export const useGetSharedLogs = () => {
    const { sharedRoutes } = useSharedRoutesStore()
    const { date } = useDateStore()
    const { role } = useAuthStore()
    const [searchParams] = useSearchParams()

    const params = useMemo(() => {
        const formattedDate = formatInTimeZone(date, 'UTC', "yyyy-MM-dd'T'HH:mm:ss")

        return [formattedDate, sharedRoutes.map((route) => route.route)]
    }, [date, sharedRoutes])

    const clParams = useMemo(() => {
        return ['controllerLogs', ...params]
    }, [params])

    return useAppQuery<Log[]>({
        queryKey: clParams,
        queryFn: ({ queryKey }) =>
            createInstance({
                url: '/controller-logs',
                method: 'GET',
                params: {
                    Date: queryKey[1],
                    Routes: queryKey[2],
                },
                paramsSerializer: {
                    indexes: null,
                },
            }),
        select: (data) => {
            if (role === Role.MileageClerk && isToday(date)) {
                const sharedLogId = searchParams.get('sharedLogId')
                return data.filter((log) => sharedLogId === log.id)
            }

            return data
        },
        refetchInterval: 1000 * 60, // Refetch every 60 seconds
        refetchIntervalInBackground: true, // Continue polling even if the tab is inactive
        refetchOnMount: false,
        enabled: params && params.length >= 2 && !!params[1] && (params[1] as string[]).length > 0,
    })
}
